// 付箋・テキストオブジェクトの本文に対する Markdown 記法（行単位のブロック記法のみ）の
// 共有パーサとスタイル定義。
//
// 方針:
// - 対象記法は「見出し（# 〜 ###### + 半角スペース）」と「箇条書き（- / * / + + 半角スペース、
//   行頭スペース 2 個ごとに 1 段ネスト）」のみ。インライン記法（**bold** 等）・番号付きリスト・
//   リンク・コードブロックは対象外。
// - `object.text` は生の Markdown テキストのまま保存し、整形は表示時のみ行う（op・domain・
//   サーバへの影響なし。編集モードでは生テキストをそのまま編集できる）。
// - DOM 表示（MarkdownTextView）・付箋フォント自動調整の計測（sticky-font-fit.ts）・
//   PDF 出力（markdown-canvas-layout.ts）・サムネイル（canvas-thumbnail.ts）の全経路が
//   本モジュールのパース結果とスタイル定数を共有し、画面と計測・出力の見た目を一致させる。

import { splitTextIntoLinkSegments } from "./linkify-text";

export type MarkdownTextLine =
  | MarkdownHeadingLine
  | MarkdownBulletLine
  | MarkdownPlainLine
  | MarkdownBlankLine;

export interface MarkdownHeadingLine {
  readonly kind: "heading";
  /** 見出しレベル（# の個数。1〜6）。 */
  readonly level: HeadingLevel;
  /** マーカーを除いた本文。 */
  readonly content: string;
}

export interface MarkdownBulletLine {
  readonly kind: "bullet";
  /** ネストレベル（floor(先頭半角スペース数 / 2) を BULLET_MAX_LEVEL でクランプ）。 */
  readonly level: number;
  /** マーカーを除いた本文（空文字列 = マーカーのみの行）。 */
  readonly content: string;
}

export interface MarkdownPlainLine {
  readonly kind: "plain";
  readonly content: string;
}

/** 空行。表示では 1 行分の高さ（基準フォントの行高）を維持する。 */
export interface MarkdownBlankLine {
  readonly kind: "blank";
}

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * 1 行を分類したうえで「生の記法プレフィックス（本文の手前）」と「本文」を分離した結果。
 * WYSIWYG 編集（markdown-wysiwyg.ts）が、記法マーカーを表示 DOM のテキストから隠しつつ
 * `rawPrefix + content` で生 Markdown を完全復元するために使う。パーサの単一の真実（本モジュール）
 * を共有し、非編集表示（parseMarkdownTextLines）と分類規則が一致することを保証する。
 *
 * 不変条件: 任意の行 `line` について `rawPrefix + content === line`（blank は両方空）。
 */
export interface MarkdownLineParts {
  readonly kind: MarkdownTextLine["kind"];
  /** heading: 1〜6 / bullet: 0〜BULLET_MAX_LEVEL / plain・blank: 0。 */
  readonly level: number;
  /** マーカーを除いた本文（blank は空文字列）。 */
  readonly content: string;
  /** 記法マーカー部分（見出し `## `・箇条書き `  - ` など）。plain・blank は空文字列。 */
  readonly rawPrefix: string;
  /** 箇条書きのネスト別グリフ（•/◦/▪）。bullet 以外は空文字列。 */
  readonly glyph: string;
}

/** 見出しレベルごとのフォント倍率（基準フォントサイズに対する em 値）。いずれも太字にする。 */
export const HEADING_FONT_SCALE: Readonly<Record<HeadingLevel, number>> = {
  1: 2,
  2: 1.5,
  3: 1.25,
  4: 1.1,
  5: 1.1,
  6: 1.1,
};

/** 箇条書きのぶら下げインデント幅（em）。ネスト 1 段ごとに同じ幅を加算する。 */
export const BULLET_INDENT_EM = 1.2;

/** 箇条書きのネストレベル上限（これを超える深さはクランプする）。 */
export const BULLET_MAX_LEVEL = 3;

/** ネストレベル別の箇条書きマーカー（上限以降は最後のマーカーを使う）。 */
export const BULLET_MARKERS: readonly string[] = ["•", "◦", "▪"];

/**
 * 本文テキストを行単位の Markdown 行へパースする。改行コードは CRLF / LF の両方を受け付ける。
 * どの記法にも該当しない行は plain としてそのまま返すため、記法を含まない既存本文の
 * 見た目は従来と変わらない。
 */
export function parseMarkdownTextLines(text: string): readonly MarkdownTextLine[] {
  return text.split(/\r?\n/).map(parseLine);
}

/**
 * 1 行を分類し、記法プレフィックスと本文を分離して返す（WYSIWYG 編集向けの公開 API）。
 * `parseLine` はこの結果から `MarkdownTextLine` を組み立てるため、分類規則は本関数に一元化される。
 * 改行は含まない 1 行を渡すこと（呼び出し側で `/\r?\n/` 分割してから渡す）。
 */
export function parseMarkdownLineParts(line: string): MarkdownLineParts {
  if (line.length === 0) {
    return { content: "", glyph: "", kind: "blank", level: 0, rawPrefix: "" };
  }
  const heading = HEADING_PATTERN.exec(line);
  if (heading && heading[1] !== undefined && heading[2] !== undefined) {
    const content = heading[2];
    const level = headingLevelFor(heading[1].length);
    // `# `（本文が空白のみ）は見出しにせず plain のまま扱う（parseLine と同じ判定）。
    if (level !== undefined && content.trim().length > 0) {
      return { content, glyph: "", kind: "heading", level, rawPrefix: `${heading[1]} ` };
    }
    return { content: line, glyph: "", kind: "plain", level: 0, rawPrefix: "" };
  }
  const bullet = BULLET_PATTERN.exec(line);
  if (bullet && bullet[1] !== undefined && bullet[2] !== undefined && bullet[3] !== undefined) {
    const level = Math.min(Math.floor(bullet[1].length / 2), BULLET_MAX_LEVEL);
    return {
      content: bullet[3],
      glyph: bulletMarkerFor(level),
      kind: "bullet",
      level,
      // 先頭スペース（ネスト）+ マーカー + 半角スペースを、入力どおりそのまま保持する。
      rawPrefix: `${bullet[1]}${bullet[2]} `,
    };
  }
  return { content: line, glyph: "", kind: "plain", level: 0, rawPrefix: "" };
}

/** ネストレベルに応じた箇条書きマーカーを返す。 */
export function bulletMarkerFor(level: number): string {
  const index = Math.min(Math.max(0, level), BULLET_MARKERS.length - 1);
  return BULLET_MARKERS[index] ?? "•";
}

/**
 * 行の表示テキスト（箇条書きはマーカー付き、見出し・plain は本文のみ）を返す。
 * DOM 表示（MarkdownTextView）と計測用ビルダー（buildMarkdownTextContent）が共有する。
 */
export function markdownLineTextFor(line: MarkdownTextLine): string {
  switch (line.kind) {
    case "heading":
    case "plain":
      return line.content;
    case "bullet":
      return line.content.length > 0
        ? `${bulletMarkerFor(line.level)} ${line.content}`
        : bulletMarkerFor(line.level);
    case "blank":
      return "";
    default:
      return assertNever(line);
  }
}

/**
 * 行の「画面に見える」表示テキストを返す。markdownLineTextFor の結果から、表示テキスト付き
 * リンク（`[テキスト](URL)`）の記法を、非編集表示（MarkdownTextView -> LinkifiedText）と同じく
 * 表示テキストへ縮約する（素の URL はそのまま残す）。
 * 画面表示は LinkifiedText を通して `[label](url)` を `label` に縮約するため、付箋フォント
 * 自動調整の計測（buildMarkdownTextContent）・Canvas 出力（layoutMarkdownTextForCanvas）は
 * raw の記法長ではなくこの表示長で扱わないと、画面とフィット・PDF・サムネイルがずれる。
 */
export function markdownLineDisplayTextFor(line: MarkdownTextLine): string {
  return linkifiedDisplayText(markdownLineTextFor(line));
}

/**
 * 行のスタイル（CSS 値）を返す。DOM 表示と計測用ビルダーが共有する単一の真実。
 * - 見出し: フォント倍率（em）+ 太字
 * - 箇条書き: ぶら下げインデント（paddingLeft + 負の textIndent）+ 常に左寄せ
 *   （インデント構造が中央 / 右寄せと両立しないため。見出し・plain はコンテナの
 *   textAlign を継承する）
 */
export function markdownLineStyleFor(line: MarkdownTextLine): MarkdownLineStyle {
  switch (line.kind) {
    case "heading":
      return {
        fontSize: `${HEADING_FONT_SCALE[line.level]}em`,
        fontWeight: "bold",
      };
    case "bullet":
      return {
        paddingLeft: `${roundEm((line.level + 1) * BULLET_INDENT_EM)}em`,
        textAlign: "left",
        textIndent: `${-BULLET_INDENT_EM}em`,
      };
    case "plain":
    case "blank":
      return {};
    default:
      return assertNever(line);
  }
}

export interface MarkdownLineStyle {
  readonly fontSize?: string | undefined;
  readonly fontWeight?: string | undefined;
  readonly paddingLeft?: string | undefined;
  readonly textAlign?: "left" | undefined;
  readonly textIndent?: string | undefined;
}

/**
 * パース済みの行を、表示（MarkdownTextView）と同じ構造の DOM として element 配下へ構築する。
 * 付箋フォント自動調整（sticky-font-fit.ts）の隠し計測要素が「表示と同じ整形構造」で
 * 計測できるようにするためのビルダー。
 *
 * innerHTML / dangerouslySetInnerHTML は使わず createElement + textContent のみで構築し、
 * ユーザー入力テキストが HTML として解釈される余地を構造的に排除する。
 * 表示側との構造一致はパリティテスト（markdown-text-view.test.tsx）で担保する。
 */
export function buildMarkdownTextContent(
  element: HTMLElement,
  lines: readonly MarkdownTextLine[]
): void {
  const ownerDocument = element.ownerDocument;
  element.replaceChildren();
  for (const line of lines) {
    const lineElement = ownerDocument.createElement("div");
    const style = markdownLineStyleFor(line);
    if (style.fontSize !== undefined) lineElement.style.fontSize = style.fontSize;
    if (style.fontWeight !== undefined) lineElement.style.fontWeight = style.fontWeight;
    if (style.paddingLeft !== undefined) lineElement.style.paddingLeft = style.paddingLeft;
    if (style.textAlign !== undefined) lineElement.style.textAlign = style.textAlign;
    if (style.textIndent !== undefined) lineElement.style.textIndent = style.textIndent;
    const lineText = markdownLineDisplayTextFor(line);
    if (lineText.length > 0) {
      lineElement.textContent = lineText;
    } else {
      // 空行は <br> で 1 行分の高さを維持する（空 div は高さ 0 になるため）。
      // 空白のみの行は plain として textContent が入り、pre-wrap コンテナ側で高さが出る。
      lineElement.appendChild(ownerDocument.createElement("br"));
    }
    element.appendChild(lineElement);
  }
}

/**
 * 各行の記法マーカー（`# ` / `- ` 等）を除去し、表示テキスト付きリンクは表示テキストへ縮約した
 * うえで、改行区切りを保持したままの本文を返す。サムネイルの先頭 24 文字プレビューなど、
 * 整形せずに「画面に見える内容」だけを見せたい箇所で使う（画面表示と同じく `[label](url)` は
 * `label` になる）。
 */
export function stripMarkdownMarkersForDisplay(text: string): string {
  return parseMarkdownTextLines(text)
    .map((line) => (line.kind === "blank" ? "" : linkifiedDisplayText(line.content)))
    .join("\n");
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

/**
 * 表示テキスト（1 行分）から、表示テキスト付きリンク（`[テキスト](URL)`）を LinkifiedText と
 * 同じく表示テキストへ縮約する。素の URL は value===url のためそのまま残る。
 */
function linkifiedDisplayText(text: string): string {
  return splitTextIntoLinkSegments(text)
    .map((segment) => segment.value)
    .join("");
}

// 見出し: 行頭の # 1〜6 個 + 半角スペース + 本文（空白のみの本文は見出しにしない）。
// 行頭に字下げがある # は見出しとして扱わない（箇条書きネストの解釈と衝突するため）。
const HEADING_PATTERN = /^(#{1,6}) (.*)$/;

// 箇条書き: 行頭の半角スペース（ネスト）+ マーカー（- / * / +）+ 半角スペース + 本文。
// タブ字下げは ( *) にマッチしないため自然に plain 扱いになる。
const BULLET_PATTERN = /^( *)([-*+]) (.*)$/;

function parseLine(line: string): MarkdownTextLine {
  const parts = parseMarkdownLineParts(line);
  switch (parts.kind) {
    case "blank":
      return { kind: "blank" };
    case "heading": {
      const level = headingLevelFor(parts.level);
      // parts.kind==="heading" は level 1〜6 を保証するが、型を HeadingLevel へ絞るため再確認する。
      if (level === undefined) return { content: parts.content, kind: "plain" };
      return { content: parts.content, kind: "heading", level };
    }
    case "bullet":
      return { content: parts.content, kind: "bullet", level: parts.level };
    case "plain":
      return { content: parts.content, kind: "plain" };
    default:
      return assertNever(parts.kind);
  }
}

// 浮動小数の掛け算誤差（例: 3 × 1.2 = 3.5999…）が CSS 値へ漏れないよう丸める。
function roundEm(value: number): number {
  return Math.round(value * 1000) / 1000;
}

// 正規表現の量指定子（#{1,6}）が保証する個数を、型アサーションを使わずに
// HeadingLevel へ絞り込むためのマッピング。範囲外は undefined を返す。
function headingLevelFor(count: number): HeadingLevel | undefined {
  switch (count) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 4:
      return 4;
    case 5:
      return 5;
    case 6:
      return 6;
    default:
      return undefined;
  }
}

function assertNever(value: never): never {
  throw new Error(`unexpected markdown line: ${JSON.stringify(value)}`);
}
